import { createMachine } from 'xstate';
import { pure } from 'xstate/lib/actions';

import getBootstrapGuards from 'common/bento/lib/bootstrap/getBootstrapGuards';
import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import locales from '../locales';
import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',

  IDENTITY_REVIEW_ERROR = 'identity-review-error',

  IDENTITY_FORM = 'identity-form',
  IDENTITY_RECAP = 'identity-recap',
  IDENTITY_SEND = 'identity-send',

  RESIDENCY_DOCUMENT = 'residency-document',

  GUIDE = 'guide',
  BIOMETRIC_CONSENT = 'biometric-consent',

  START_IDENTITY_VERIFICATION = 'start-identity-verification',

  ONFIDO_NATIVE_IDENTITY_CHECK = 'onfido-native-identity-check',
  ONFIDO_NATIVE_IDENTITY_ERROR = 'onfido-native-identity-error',

  FOURTHLINE_NATIVE_WORKFLOW = 'fourthline-native-workflow',

  ONFIDO_IDENTITY_CHECK = 'onfido-identity-check',
  FOURTHLINE_ID_CHECK = 'fourthline-id-check',
  POST_FOURTHLINE_ID_CHECK_REDIRECTION = 'post-fourthline-id-check-redirection',

  FINISH_IDENTITY_VERIFICATION = 'finish-identity-verification',

  SUCCESS = 'success',

  DONE = 'done',
  ABORT = 'abort',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'identity',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
      // Silence those events if fired outside of the states handling it
      FOURTHLINE_SUCCESS: {},
      RESTART_ID_CHECK: {},
    },

    states: {
      [State.INIT]: {
        always: [
          // Allows to jump into `post-fourthline-id-check-redirection` if the URL includes `bootstrap:identity=post-fourthline-id-check-redirection`
          // Useful on mobile, as we instantly call finishIdentityVerification when coming back from Fourthline with a redirectUrl including these params.
          ...getBootstrapGuards([State.POST_FOURTHLINE_ID_CHECK_REDIRECTION]),

          {
            cond: Guard.IsIdentityReviewInvalid,
            target: State.IDENTITY_REVIEW_ERROR,
          },
          {
            cond: Guard.ShouldSeeRecap,
            target: State.IDENTITY_RECAP,
          },
          {
            target: State.IDENTITY_FORM,
          },
        ],
      },

      [State.IDENTITY_REVIEW_ERROR]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.IDENTITY_FORM,
          PREV: State.ABORT,
        },
      },

      [State.IDENTITY_FORM]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: Guard.IsIdentityReviewInvalid,
              target: State.IDENTITY_REVIEW_ERROR,
            },
            {
              target: State.ABORT,
            },
          ],
          SUBMIT: State.IDENTITY_SEND,
        },
      },

      [State.IDENTITY_RECAP]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: [
            {
              cond: Guard.ShouldGoThroughResidencyDocumentQuestions,
              target: State.RESIDENCY_DOCUMENT,
            },
            {
              actions: Action.AssignDefaultIdDocumentCase,
              target: State.GUIDE,
            },
          ],
          PREV: State.ABORT,
        },
      },

      [State.IDENTITY_SEND]: {
        invoke: {
          onDone: [
            {
              cond: Guard.ShouldGoThroughResidencyDocumentQuestions,
              target: State.RESIDENCY_DOCUMENT,
            },
            {
              actions: Action.AssignDefaultIdDocumentCase,
              target: State.GUIDE,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.IDENTITY_FORM,
          },
          src: Service.UpdateIdentity,
        },
      },

      [State.RESIDENCY_DOCUMENT]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: Guard.ShouldSeeRecap,
              target: State.IDENTITY_RECAP,
            },
            {
              target: State.IDENTITY_FORM,
            },
          ],
          SUBMIT_HAS_NO_RESIDENCY_DOCUMENT: {
            actions: Action.AssignIdDocumentCase,
            target: State.GUIDE,
          },
          SUBMIT_HAS_RESIDENCY_CARD: {
            actions: Action.AssignIdDocumentCase,
            target: State.GUIDE,
          },
          SUBMIT_HAS_VISA: {
            actions: Action.AssignIdDocumentCase,
            target: State.GUIDE,
          },
        },
      },

      [State.GUIDE]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.BIOMETRIC_CONSENT,
          PREV: [
            {
              cond: Guard.ShouldSeeRecap,
              target: State.IDENTITY_RECAP,
            },
            { target: State.IDENTITY_FORM },
          ],
        },
      },

      [State.BIOMETRIC_CONSENT]: {
        entry: [
          shared.actions.trackView(),
          shared.actions.serverTrack({
            event: 'Onboarding Biometric Consent Asked',
          }),
        ],

        on: {
          NEXT: [
            {
              actions: shared.actions.serverTrack({
                event: 'Onboarding Biometric Consent Accepted',
              }),
              target: State.START_IDENTITY_VERIFICATION,
            },
          ],
          PREV: State.GUIDE,
        },
      },

      [State.START_IDENTITY_VERIFICATION]: {
        invoke: {
          onDone: [
            {
              actions: Action.AssignIdentityCheckContext,
              cond: Guard.ShouldDoNativeFourthlineWorkflow,
              target: State.FOURTHLINE_NATIVE_WORKFLOW,
            },
            {
              actions: Action.AssignIdentityCheckContext,
              cond: Guard.ShouldDoFourthlineVerification,
              target: State.FOURTHLINE_ID_CHECK,
            },
            {
              actions: Action.AssignIdentityCheckContext,
              cond: shared.guards.isInWebView,
              target: State.ONFIDO_NATIVE_IDENTITY_CHECK,
            },
            {
              actions: Action.AssignIdentityCheckContext,
              target: State.ONFIDO_IDENTITY_CHECK,
            },
          ],
          onError: [
            {
              actions: shared.actions.toast({
                status: 'success',
                title: locales.errors.idStepAlreadyDone,
              }),
              cond: Guard.IsIdStepAlreadyDoneError,
              target: State.ABORT,
            },
            {
              actions: shared.actions.error(),
              target: State.ABORT,
            },
          ],
          src: Service.StartIdentityVerification,
        },
      },

      [State.FOURTHLINE_NATIVE_WORKFLOW]: {
        // This state shows the native ID check screen, we want to track when it's shown
        entry: shared.actions.trackView(),

        invoke: {
          onDone: [
            {
              cond: Guard.HasNativeIdCheckFailed,
              target: State.ONFIDO_NATIVE_IDENTITY_ERROR,
            },
            {
              cond: Guard.HasNativeIdCheckSucceeded,
              target: State.DONE,
            },
            { target: State.IDENTITY_FORM },
          ],

          onError: {
            target: State.ONFIDO_NATIVE_IDENTITY_ERROR,
          },

          src: Service.CompleteNativeFourthlineWorkflow,
        },

        on: {
          PREV: State.GUIDE,
        },
      },

      [State.FOURTHLINE_ID_CHECK]: {
        entry: shared.actions.trackView(),

        on: {
          FOURTHLINE_SUCCESS: State.FINISH_IDENTITY_VERIFICATION,
          PREV: State.IDENTITY_FORM,
          /**
           * Refetch a new Fourthline token and restart the SDK
           */
          RESTART_ID_CHECK: State.START_IDENTITY_VERIFICATION,
        },
      },

      [State.ONFIDO_NATIVE_IDENTITY_CHECK]: {
        // This state shows the native ID check screen, we want to track when it's shown
        entry: shared.actions.trackView(),

        invoke: {
          onDone: [
            {
              cond: Guard.HasNativeIdCheckFailed,
              target: State.ONFIDO_NATIVE_IDENTITY_ERROR,
            },
            {
              cond: Guard.HasNativeIdCheckSucceeded,
              target: State.DONE,
            },
            { target: State.IDENTITY_FORM },
          ],

          onError: {
            target: State.ONFIDO_NATIVE_IDENTITY_ERROR,
          },

          src: Service.CompleteNativeIdCheck,
        },

        on: {
          PREV: State.GUIDE,
        },
      },

      [State.ONFIDO_NATIVE_IDENTITY_ERROR]: {
        on: {
          PREV: State.IDENTITY_FORM,
        },
      },

      [State.ONFIDO_IDENTITY_CHECK]: {
        entry: shared.actions.trackView(),

        on: {
          ONFIDO_SUCCESS: State.FINISH_IDENTITY_VERIFICATION,
          PREV: State.IDENTITY_FORM,
        },
      },

      [State.POST_FOURTHLINE_ID_CHECK_REDIRECTION]: {
        always: {
          actions: Action.AssignFourthlineIdCheckContextPostRedirection,
          target: State.FINISH_IDENTITY_VERIFICATION,
        },
      },

      [State.FINISH_IDENTITY_VERIFICATION]: {
        invoke: {
          onDone: {
            actions: pure((context) =>
              context.idCheckContext
                ? shared.actions.track({
                    name: 'Onboarding ID Completed',
                    properties: {
                      idSupplier: context.idCheckContext.provider.toLowerCase(),
                    },
                  })
                : undefined,
            ),
            target: State.SUCCESS,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.ABORT,
          },
          src: Service.FinishIdentityVerification,
        },
      },

      [State.SUCCESS]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.DONE,
          PREV: State.DONE,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
